<template>
    <div class="diagnostic-discussions-view container mt-3">
      <h1 class="mb-4">Discussions Diagnostiques / Templates</h1>
  
      <!-- Add New Discussion Form -->
      <section class="add-discussion-section card card-body mb-4 shadow-sm">
        <h2 class="h5 mb-3">Ajouter une Nouvelle Discussion</h2>
        <form @submit.prevent="addDiscussion">
          <div class="mb-3">
            <label for="newTitle" class="form-label">Titre</label>
            <input type="text" class="form-control form-control-sm" id="newTitle" v-model="newDiscussion.title" required placeholder="Titre court et descriptif...">
          </div>
          <div class="mb-3">
            <label for="newContent" class="form-label">Contenu / Texte</label>
            <textarea class="form-control form-control-sm" id="newContent" v-model="newDiscussion.content" required rows="4" placeholder="Collez ou écrivez le texte complet ici..."></textarea>
          </div>
           <div class="mb-3">
            <label for="newTags" class="form-label">Tags (mots-clés séparés par virgule)</label>
            <input type="text" class="form-control form-control-sm" id="newTags" v-model="newDiscussion.tags" placeholder="ex: SCA, douleur thoracique, ECG, troponine...">
          </div>
          <div class="text-end">
               <button type="submit" class="btn btn-primary btn-sm" :disabled="addingDiscussion">
                   <span v-if="addingDiscussion" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {{ addingDiscussion ? 'Ajout...' : 'Ajouter Discussion' }}
               </button>
          </div>
           <p v-if="addError" class="error-message small mt-2">{{ addError }}</p>
        </form>
      </section>
  
      <hr>
  
      <!-- Search and List Section -->
      <section class="discussion-list-section mt-4">
          <h2 class="h5 mb-3">Rechercher & Copier</h2>
          <!-- Search Input -->
          <div class="input-group mb-4">
               <span class="input-group-text">Rechercher:</span>
               <input type="search" class="form-control" placeholder="Titre, contenu, tag..." v-model="searchTerm" @input="debouncedSearch">
          </div>
  
           <!-- Loading/Error/Empty states -->
          <div v-if="loadingList" class="d-flex justify-content-center my-4">
               <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Chargement...</span></div>
          </div>
          <div v-else-if="fetchError" class="alert alert-danger" role="alert">Erreur: {{ fetchError }}</div>
          <div v-else-if="!filteredDiscussions || filteredDiscussions.length === 0" class="text-center text-muted p-3 border rounded bg-light">
               {{ searchTerm ? 'Aucune discussion trouvée pour "' + searchTerm + '".' : 'Aucune discussion enregistrée.' }}
          </div>
  
          <!-- Discussion List - MODIFIED STRUCTURE -->
        <div v-if="!loadingList && !fetchError && filteredDiscussions && filteredDiscussions.length > 0" class="discussion-list">
            <div v-for="discussion in filteredDiscussions" :key="discussion.id" class="card discussion-card mb-3 shadow-sm">
                 <div class="card-header bg-light py-2 px-3">
                    <div class="d-flex justify-content-between align-items-center">
                        <h5 class="h6 mb-0 discussion-title">{{ discussion.title }}</h5>
                        <!-- Action buttons in header -->
                        <div>
                            <button @click="toggleExpandDiscussion(discussion.id)" class="btn btn-sm btn-outline-secondary me-2">
                                {{ expandedDiscussionId === discussion.id ? 'Réduire' : 'Lire' }}
                            </button>
                            <button @click="copyText(discussion.content)" class="btn btn-sm btn-outline-primary copy-button-list" title="Copier le contenu">
                                Copier
                            </button>
                             <!-- Optional Edit/Delete buttons could go here -->
                        </div>
                    </div>
                 </div>
                 <!-- Body shows preview or full content -->
                 <div class="card-body p-3">
                    <p v-if="expandedDiscussionId !== discussion.id" class="mb-1 small discussion-content-preview text-muted">
                        {{ truncateText(discussion.content, 150) }}
                    </p>
                    <!-- Use v-show for smoother expand/collapse without re-rendering -->
                    <pre v-show="expandedDiscussionId === discussion.id" class="discussion-content-full bg-white p-2 border rounded small"><code>{{ discussion.content }}</code></pre>

                    <small v-if="discussion.tags && Array.isArray(discussion.tags) && discussion.tags.length > 0" class="text-muted discussion-tags d-block mt-2">
                        Tags: {{ discussion.tags.join(', ') }}
                    </small>
                 </div>
            </div>
        </div>
      </section>
  
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, computed, onMounted } from 'vue';
  import { supabase } from '../../supabase.js';
  import { debounce } from 'lodash-es'; // npm install lodash-es
  
  const discussions = ref([]);
  const loadingList = ref(false);
  const fetchError = ref('');
  const searchTerm = ref('');
  // --- NEW State for expanded item ---
const expandedDiscussionId = ref(null); // Holds the ID of the currently expanded discussion
  const newDiscussion = reactive({
      title: '',
      content: '',
      tags: '' // Store as comma-separated string initially
  });
  const addingDiscussion = ref(false);
  const addError = ref('');
  
  // --- Computed property for filtering ---
  const filteredDiscussions = computed(() => {
      // Filtering is now done server-side via Edge Function query param
      // This computed prop just returns the fetched list
      // If you wanted client-side filtering *as well*:
      // const lowerSearch = searchTerm.value.toLowerCase();
      // if (!lowerSearch) return discussions.value;
      // return discussions.value.filter(d =>
      //    d.title?.toLowerCase().includes(lowerSearch) ||
      //    d.content?.toLowerCase().includes(lowerSearch) ||
      //    d.tags?.some(tag => tag.toLowerCase().includes(lowerSearch))
      // );
      return discussions.value; // Return the list fetched from the function
  });
  
  // --- Methods ---
  const fetchDiscussions = async () => {
      loadingList.value = true;
      fetchError.value = '';
      console.log(`Fetching discussions, search term: "${searchTerm.value}"`);
      try {
           // Pass search term to Edge Function
          const functionName = `get-discussions${searchTerm.value ? '?search=' + encodeURIComponent(searchTerm.value) : ''}`;
          const { data, error } = await supabase.functions.invoke(functionName);
  
          if (error) throw error;
          discussions.value = Array.isArray(data) ? data : [];
      } catch (err) {
          console.error("Erreur fetchDiscussions:", err);
          let msg = err.message; if (err.context?.json?.error) msg = err.context.json.error;
          fetchError.value = `Impossible de charger: ${msg}`;
          discussions.value = [];
      } finally {
          loadingList.value = false;
      }
  };
  
  // Debounce the search fetch request
  const debouncedSearch = debounce(fetchDiscussions, 400); // Wait 400ms after user stops typing
  
  const addDiscussion = async () => {
      if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) {
          addError.value = "Le titre et le contenu sont requis.";
          return;
      }
      addingDiscussion.value = true;
      addError.value = '';
      try {
          // Prepare tags as an array of strings, removing empty ones
          const tagsArray = newDiscussion.tags
                              .split(',')
                              .map(tag => tag.trim())
                              .filter(tag => tag);
  
          const payload = {
              title: newDiscussion.title.trim(),
              content: newDiscussion.content.trim(),
              tags: tagsArray
          };
  
          const { data: newEntry, error } = await supabase.functions.invoke('add-discussion', {
              body: payload
          });
          if (error) throw error;
  
          // Add to local list immediately or refetch
          // discussions.value.unshift(newEntry); // Add to top
          // discussions.value.sort((a, b) => a.title.localeCompare(b.title)); // Keep sorted
          await fetchDiscussions(); // Easiest to refetch to ensure consistency and sorting
  
          // Clear form
          newDiscussion.title = '';
          newDiscussion.content = '';
          newDiscussion.tags = '';
  
      } catch (err) {
          console.error("Erreur addDiscussion:", err);
          let msg = err.message; if (err.context?.json?.error) msg = err.context.json.error;
          addError.value = `Erreur ajout: ${msg}`;
      } finally {
          addingDiscussion.value = false;
      }
  };
  
  const copyText = (content) => {
      if (!content) return;
      navigator.clipboard.writeText(content).then(() => {
          alert('Texte copié !');
      }).catch(err => {
          console.error('Erreur copie:', err);
          alert('Erreur copie.');
      });
  };
  
  const truncateText = (text, maxLength) => {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
  };
  // --- NEW Method to toggle expansion ---
const toggleExpandDiscussion = (discussionId) => {
    if (expandedDiscussionId.value === discussionId) {
        expandedDiscussionId.value = null; // Collapse if already expanded
    } else {
        expandedDiscussionId.value = discussionId; // Expand this one
    }
};

  
  // --- Lifecycle ---
  onMounted(() => {
      fetchDiscussions(); // Initial fetch
  });
  
  </script>
  
  <style scoped>
  .diagnostic-discussions-view {
      /* Container styles inherited */
  }
  .add-discussion-section {
      background-color: var(--component-background-alt); /* Slightly different background */
  }
  .discussion-list .list-group-item {
      cursor: default; /* Make it clear list items aren't clickable by default */
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
  }
   .discussion-list .list-group-item:hover {
       background-color: #fff; /* Less pronounced hover */
   }
  .discussion-title {
      color: var(--primary-color);
      font-weight: 500;
      margin-bottom: 0.3rem !important;
  }
  .discussion-content-preview {
      color: var(--text-color-muted);
      font-size: 0.9em;
      line-height: 1.4;
      margin-bottom: 0.5rem !important;
  }
  .discussion-tags {
      font-size: 0.75em;
      font-style: italic;
      color: var(--secondary-color);
  }
  .copy-button-list {
       padding: 0.1rem 0.5rem; /* Smaller copy button */
       font-size: 0.75rem;
       line-height: 1;
       align-self: flex-start; /* Align top right */
  }
  .alert-sm { /* Consistent small alert */
      padding: 0.5rem 0.75rem;
      font-size: 0.85em;
  }
  </style>